import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../context";
import { canAccessPremium, isAdminUser } from "../utils/adminCheck";

// Single source of truth for "can this user use premium features right now?".
// Matches the product rule EXACTLY:
//   • admins always,
//   • genuine PAID subscribers (canAccessPremium: paid founder tiers, paid public
//     plans, premium_seat, is_premium/entitlements) — NOT plain Founder Basic,
//   • OR the site-wide free-premium launch window is active (everyone, free).
// So during launch week every tool opens for everyone; when the window ends it
// auto-reverts to paid-only. Every premium gate should use THIS so web + backend
// (resolve_entitlement) stay in lockstep.
//
// Window state (`/premium-preview/active`, same endpoint as the countdown banner)
// is cached module-wide 60s so switching tools doesn't refetch or flash.
let _win = { active: null, at: 0 };

export function usePremiumAccess(user) {
  const [windowActive, setWindowActive] = useState(_win.active);

  useEffect(() => {
    let alive = true;
    if (_win.active !== null && Date.now() - _win.at < 60000) {
      setWindowActive(_win.active);
      return undefined;
    }
    axios.get(`${API}/premium-preview/active`)
      .then((r) => {
        const active = !!r.data?.active
          && (!r.data.ends_at || new Date(r.data.ends_at).getTime() > Date.now());
        _win = { active, at: Date.now() };
        if (alive) setWindowActive(active);
      })
      .catch(() => { if (alive) setWindowActive(false); });
    return () => { alive = false; };
  }, []);

  const premium = isAdminUser(user) || canAccessPremium(user) || windowActive === true;
  return { premium, windowActive, loading: windowActive === null };
}

export default usePremiumAccess;
